import axios from 'axios';
import {get, post} from './http';

export type SwitchView = {
  key: string;
  state: 'enabled' | 'killed';
  description: string | null;
  owner: string | null;
  kill_switch: boolean;
  permanent: boolean;
  remove_by: string | null;
  in_manifest: boolean;
  // Targeting the editor cannot represent; kill and clear still work, the lists below are null.
  custom: boolean;
  users: string[] | null;
  roles: string[] | null;
  tenants: string[] | null;
  percentage: number | null;
  // Non-null for a multi-variant switch, which the editor cannot change either.
  variants: string[] | null;
  default_variant: string | null;
  weights: Record<string, number> | null;
  checked_per: 'item' | 'user' | null;
  parent: string | null;
};

export type SwitchList = {generation: number; switches: SwitchView[]};

export type SwitchVersion = {
  generation: number;
  updated_at: string;
  actor: string;
  reason: string | null;
  summary: string | null;
};

export type SwitchTargeting = {users: string[]; roles: string[]; percentage: number};

export type SwitchProposalChange = {key: string; targeting: SwitchTargeting; base: SwitchTargeting};

export type SwitchProposal = {
  id: string;
  switch_key: string;
  kind: 'targeting' | 'clear' | 'delete';
  status: 'pending' | 'approved' | 'rejected';
  author: string;
  reason: string;
  submitted_at: string;
  decided_by: string | null;
  decided_at: string | null;
  changes: SwitchProposalChange[];
};

export type ProposeSwitchChange = {
  kind: SwitchProposal['kind'];
  reason: string;
  changes?: Array<{key: string} & SwitchTargeting>;
};

const path = (key: string) => `/switches/${encodeURIComponent(key)}`;

export function isSwitchConflict(error: unknown): boolean {
  return axios.isAxiosError(error) && error.response?.status === 409;
}

export const switchesApi = {
  async list() {
    return get<SwitchList>('/switches');
  },
  async history(limit = 50) {
    const data = await get<{versions: SwitchVersion[]}>('/switches/history', {limit});
    return data.versions;
  },
  async kill(key: string, reason: string) {
    await post(`${path(key)}/kill`, {reason});
  },
  async proposals() {
    return get<SwitchProposal[]>('/switches/proposals');
  },
  async propose(key: string, payload: ProposeSwitchChange) {
    return post<ProposeSwitchChange, SwitchProposal>(`${path(key)}/proposals`, payload);
  },
  async approve(id: string) {
    await post(`/switches/proposals/${encodeURIComponent(id)}/approve`);
  },
  async reject(id: string) {
    await post(`/switches/proposals/${encodeURIComponent(id)}/reject`);
  }
};
